// LeadAgentUsage.service.ts owns the HTTP call for the Lead Agent usage/limit
// banner (C8 / RET-1344). The endpoint is a bff-web proxy to workspace-service,
// which composes leadbilling consumption + tier into a client-safe capacity
// (capacityPct/tier/resetDate/isPaused/hasCap; never token counts).

import { AxiosService } from 'src/axios/axios.service';
import { LEAD_AGENT_TIERS } from './LeadAgentAcceptance.functions';
import type {
  tLeadAgentAcceptanceEnvelope,
  tLeadAgentTier,
  tLeadAgentUsage,
} from './LeadAgentAcceptance.types';

/**
 * Runtime-validate the wire `data` into `tLeadAgentUsage`. Returns null when the
 * payload is missing/malformed so the provider fails closed (no banner) instead
 * of trusting a fabricated shape. No `as unknown as` — every field is checked.
 */
function parseLeadAgentUsage(data: unknown): tLeadAgentUsage | null {
  if (typeof data !== 'object' || data === null) return null;
  const raw = data as Record<string, unknown>;

  const capacityPct = raw.capacityPct;
  if (typeof capacityPct !== 'number' || !Number.isFinite(capacityPct)) {
    return null;
  }
  if (typeof raw.hasCap !== 'boolean') return null;
  if (typeof raw.isPaused !== 'boolean') return null;

  // Validate the tier against the enum; anything else collapses to null rather
  // than leaking an invalid string into the banner copy.
  const tier =
    typeof raw.tier === 'string' &&
    (LEAD_AGENT_TIERS as readonly string[]).includes(raw.tier)
      ? (raw.tier as tLeadAgentTier)
      : null;

  const resetDate =
    typeof raw.resetDate === 'string' && raw.resetDate !== ''
      ? raw.resetDate
      : null;

  return {
    capacityPct: Math.min(100, Math.max(0, Math.round(capacityPct))),
    tier,
    resetDate,
    isPaused: raw.isPaused,
    hasCap: raw.hasCap,
    // RET-1633 (U7/U9): additive trial-cap markers. Absent on older bff deploys
    // → false (fail closed: no unlock-offer fetch, no trial-cap banner).
    isTrialCap: raw.isTrialCap === true,
    extended: raw.extended === true,
  };
}

export const LeadAgentUsageService = Object.freeze({
  /**
   * GET /workspace/{wid}/lead-agent/usage
   *
   * Returns the client-safe capacity, or null when there is no cap / not an
   * LA-paying workspace / any error (fail closed: no banner). A non-2xx (e.g.
   * 503 when leadbilling is down) rejects inside AxiosService; the provider's
   * catch turns it into null. `hasCap === false` is a legitimate "no banner".
   */
  getUsage: async function (
    workspaceId: string,
  ): Promise<tLeadAgentUsage | null> {
    const response = await AxiosService.get<tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/usage`,
      { isBFFService: true },
    );

    if (!response?.success || !response.data) return null;
    return parseLeadAgentUsage(response.data);
  },
});
